import React, { useState, useEffect } from 'react';
import {
    X, Users, TrendingUp, Clock, CheckCircle2, AlertTriangle,
    Download, ChevronDown, Search, BarChart3, Lightbulb, ArrowUpRight,
    ArrowDownRight, Filter, FileText, Table2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const CourseAnalyticsDashboard = ({ isOpen, onClose, course }) => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [analytics, setAnalytics] = useState(null);
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [bottlenecks, setBottlenecks] = useState([]);
    const [sortField, setSortField] = useState('completion_rate');
    const [sortDir, setSortDir] = useState('asc');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeTimeline, setEmployeeTimeline] = useState([]);

    useEffect(() => {
        if (isOpen && course) {
            fetchAnalytics();
        }
    }, [isOpen, course]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [analyticsRes, itemsRes, employeesRes, bottlenecksRes] = await Promise.all([
                api.get(`/courses/${course.id}/analytics`, { headers }),
                api.get(`/courses/${course.id}/analytics/items`, { headers }),
                api.get(`/courses/${course.id}/analytics/employees`, { headers }),
                api.get(`/courses/${course.id}/analytics/bottlenecks`, { headers })
            ]);

            setAnalytics(analyticsRes.data.analytics);
            setItems(itemsRes.data.items || []);
            setEmployees(employeesRes.data.employees || []);
            setBottlenecks(bottlenecksRes.data.bottlenecks || []);
        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployeeTimeline = async (enrollmentId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`/courses/${course.id}/analytics/employees/${enrollmentId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEmployeeTimeline(response.data.timeline || []);
        } catch (error) {
            console.error('Error fetching employee timeline:', error);
        }
    };

    const handleExport = async (format) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post(`/courses/${course.id}/analytics/export`, { format }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: format === 'csv' ? 'blob' : 'json'
            });

            if (format === 'csv') {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `course-${course.id}-analytics.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success('CSV exported successfully');
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export');
        }
    };

    const sortedItems = [...items].sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

    const filteredEmployees = employees.filter(emp => {
        const matchesStatus = statusFilter === 'all' || emp.status === statusFilter;
        const matchesSearch = !searchTerm ||
            emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.email.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white w-full h-full flex flex-col">
                {/* Header */}
                <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-white flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Course Analytics</h2>
                            <p className="text-sm text-gray-600">{course?.name}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Tabs */}
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                            {['overview', 'items', 'employees', 'insights'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors capitalize ${activeTab === tab
                                            ? 'bg-white text-gray-800 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-800'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Export */}
                        <div className="relative group">
                            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors">
                                <Download className="w-4 h-4" />
                                Export
                                <ChevronDown className="w-3 h-3" />
                            </button>
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                <button
                                    onClick={() => handleExport('csv')}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <Table2 className="w-4 h-4" />
                                    Export CSV
                                </button>
                                <button
                                    onClick={() => handleExport('pdf')}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <FileText className="w-4 h-4" />
                                    Export PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                        </div>
                    ) : (
                        <div className="max-w-7xl mx-auto">
                            {/* Overview Tab */}
                            {activeTab === 'overview' && (
                                <div className="space-y-6">
                                    {/* Stats Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <StatCard
                                            icon={Users}
                                            label="Total Enrolled"
                                            value={analytics?.metrics?.total_enrolled || 0}
                                            color="blue"
                                        />
                                        <StatCard
                                            icon={CheckCircle2}
                                            label="Completion Rate"
                                            value={`${analytics?.metrics?.completion_rate || 0}%`}
                                            color="green"
                                            trend={analytics?.metrics?.completion_rate > 50 ? 'up' : 'down'}
                                        />
                                        <StatCard
                                            icon={TrendingUp}
                                            label="Avg Progress"
                                            value={`${analytics?.metrics?.avg_progress || 0}%`}
                                            color="purple"
                                        />
                                        <StatCard
                                            icon={Clock}
                                            label="Avg Time to Complete"
                                            value={`${analytics?.metrics?.avg_completion_days || 0} days`}
                                            color="orange"
                                        />
                                    </div>

                                    {/* Quick Stats */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="bg-white rounded-xl p-5 border border-gray-200">
                                            <h3 className="font-semibold text-gray-800 mb-3">Enrollment Status</h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-gray-600">Active</span>
                                                    <span className="font-medium text-green-600">{analytics?.metrics?.active || 0}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-gray-600">Completed</span>
                                                    <span className="font-medium text-blue-600">{analytics?.metrics?.completed || 0}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-gray-600">Paused</span>
                                                    <span className="font-medium text-yellow-600">{analytics?.metrics?.paused || 0}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white rounded-xl p-5 border border-gray-200">
                                            <h3 className="font-semibold text-gray-800 mb-3">Item Performance</h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-gray-600">Total Items</span>
                                                    <span className="font-medium">{items.length}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-gray-600">Low Completion</span>
                                                    <span className="font-medium text-red-600">
                                                        {items.filter(i => i.completion_rate < 50 && i.times_released > 0).length}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white rounded-xl p-5 border border-gray-200">
                                            <h3 className="font-semibold text-gray-800 mb-3">AI Insights</h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-gray-600">Bottlenecks Found</span>
                                                    <span className={`font-medium ${bottlenecks.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                        {bottlenecks.length}
                                                    </span>
                                                </div>
                                                {bottlenecks.length > 0 && (
                                                    <button
                                                        onClick={() => setActiveTab('insights')}
                                                        className="text-sm text-primary hover:underline"
                                                    >
                                                        View Recommendations →
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Items Tab */}
                            {activeTab === 'items' && (
                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                    <div className="px-5 py-4 border-b border-gray-200">
                                        <h3 className="font-semibold text-gray-800">Item Performance</h3>
                                        <p className="text-sm text-gray-600">Click column headers to sort</p>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Day</th>
                                                    <th
                                                        className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                        onClick={() => handleSort('times_released')}
                                                    >
                                                        Released {sortField === 'times_released' && (sortDir === 'asc' ? '↑' : '↓')}
                                                    </th>
                                                    <th
                                                        className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                        onClick={() => handleSort('completion_rate')}
                                                    >
                                                        Completion % {sortField === 'completion_rate' && (sortDir === 'asc' ? '↑' : '↓')}
                                                    </th>
                                                    <th
                                                        className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                        onClick={() => handleSort('avg_hours_to_complete')}
                                                    >
                                                        Avg Time {sortField === 'avg_hours_to_complete' && (sortDir === 'asc' ? '↑' : '↓')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {sortedItems.map((item) => (
                                                    <tr key={item.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3">
                                                            <span className="font-medium text-gray-800">{item.title}</span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${item.item_type === 'task' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                                                                }`}>
                                                                {item.item_type}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                                                            Day {item.relative_day}
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                                                            {item.times_released}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className={`font-medium ${item.completion_rate >= 70 ? 'text-green-600' :
                                                                    item.completion_rate >= 50 ? 'text-yellow-600' : 'text-red-600'
                                                                }`}>
                                                                {item.completion_rate}%
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                                                            {item.avg_hours_to_complete}h
                                                        </td>
                                                    </tr>
                                                ))}
                                                {sortedItems.length === 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                                            No items in this course yet
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Employees Tab */}
                            {activeTab === 'employees' && (
                                <div className="space-y-4">
                                    {/* Filters */}
                                    <div className="flex items-center gap-4">
                                        <div className="relative flex-1 max-w-md">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Search employees..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                            />
                                        </div>
                                        <select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary"
                                        >
                                            <option value="all">All Status</option>
                                            <option value="active">Active</option>
                                            <option value="completed">Completed</option>
                                            <option value="paused">Paused</option>
                                        </select>
                                    </div>

                                    {/* Employee List */}
                                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                        <div className="divide-y divide-gray-100">
                                            {filteredEmployees.map((emp) => (
                                                <div
                                                    key={emp.enrollment_id}
                                                    className="p-4 hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => {
                                                        setSelectedEmployee(emp);
                                                        fetchEmployeeTimeline(emp.enrollment_id);
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                                                                {emp.name.split(' ').map(n => n[0]).join('')}
                                                            </div>
                                                            <div>
                                                                <p className="font-medium text-gray-800">{emp.name}</p>
                                                                <p className="text-sm text-gray-500">{emp.email}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-4">
                                                            <div className="text-right">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                                        <div
                                                                            className={`h-full rounded-full ${emp.progress >= 100 ? 'bg-green-500' :
                                                                                    emp.progress >= 50 ? 'bg-blue-500' : 'bg-yellow-500'
                                                                                }`}
                                                                            style={{ width: `${Math.min(emp.progress, 100)}%` }}
                                                                        />
                                                                    </div>
                                                                    <span className="text-sm font-medium text-gray-700">{emp.progress}%</span>
                                                                </div>
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    {emp.last_activity ? `Last active: ${new Date(emp.last_activity).toLocaleDateString()}` : 'No activity yet'}
                                                                </p>
                                                            </div>
                                                            <span className={`px-2 py-1 rounded text-xs font-medium ${emp.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                                    emp.status === 'active' ? 'bg-blue-100 text-blue-700' :
                                                                        'bg-yellow-100 text-yellow-700'
                                                                }`}>
                                                                {emp.status}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {filteredEmployees.length === 0 && (
                                                <div className="p-8 text-center text-gray-500">
                                                    No employees found
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Insights Tab */}
                            {activeTab === 'insights' && (
                                <div className="space-y-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <Lightbulb className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-800">AI Bottleneck Detection</h3>
                                            <p className="text-sm text-gray-600">
                                                {bottlenecks.length === 0
                                                    ? 'No issues detected in your course'
                                                    : `Found ${bottlenecks.length} item(s) that may need attention`}
                                            </p>
                                        </div>
                                    </div>

                                    {bottlenecks.length === 0 ? (
                                        <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                                            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                                            <h4 className="font-semibold text-green-800">Course is Performing Well!</h4>
                                            <p className="text-sm text-green-600 mt-1">
                                                All items have healthy completion rates
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {bottlenecks.map((bottleneck, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`bg-white rounded-xl border-l-4 p-5 ${bottleneck.severity === 'high'
                                                            ? 'border-red-500'
                                                            : 'border-yellow-500'
                                                        }`}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-start gap-3">
                                                            <AlertTriangle className={`w-5 h-5 mt-0.5 ${bottleneck.severity === 'high'
                                                                    ? 'text-red-500'
                                                                    : 'text-yellow-500'
                                                                }`} />
                                                            <div>
                                                                <p className="font-medium text-gray-800">
                                                                    {bottleneck.type === 'low_completion'
                                                                        ? 'Low Completion Rate'
                                                                        : 'Slow Completion Time'}
                                                                </p>
                                                                <p className="text-sm text-gray-600 mt-1">
                                                                    {bottleneck.recommendation}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${bottleneck.severity === 'high'
                                                                ? 'bg-red-100 text-red-700'
                                                                : 'bg-yellow-100 text-yellow-700'
                                                            }`}>
                                                            {bottleneck.severity}
                                                        </span>
                                                    </div>
                                                    <div className="mt-4 flex items-center gap-3">
                                                        <button className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-hover transition-colors">
                                                            Apply Fix
                                                        </button>
                                                        <button className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors">
                                                            Dismiss
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Employee Timeline Modal */}
                {selectedEmployee && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                                <div>
                                    <h3 className="font-bold text-gray-800">{selectedEmployee.name}'s Timeline</h3>
                                    <p className="text-sm text-gray-500">{selectedEmployee.progress}% complete</p>
                                </div>
                                <button
                                    onClick={() => setSelectedEmployee(null)}
                                    className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="p-5 overflow-y-auto max-h-[calc(80vh-120px)]">
                                <div className="space-y-3">
                                    {employeeTimeline.map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.status === 'completed' ? 'bg-green-100 text-green-600' :
                                                    item.status === 'in_progress' ? 'bg-blue-100 text-blue-600' :
                                                        item.status === 'released' ? 'bg-yellow-100 text-yellow-600' :
                                                            'bg-gray-200 text-gray-400'
                                                }`}>
                                                {item.status === 'completed' ? (
                                                    <CheckCircle2 className="w-4 h-4" />
                                                ) : (
                                                    <span className="text-xs font-medium">D{item.relative_day}</span>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-800 text-sm">{item.item_title}</p>
                                                <p className="text-xs text-gray-500 capitalize">{item.item_type} • {item.status || 'pending'}</p>
                                            </div>
                                            {item.completed_at && (
                                                <span className="text-xs text-gray-500">
                                                    {new Date(item.completed_at).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                    {employeeTimeline.length === 0 && (
                                        <p className="text-center text-gray-500 py-4">No timeline data</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color, trend }) => {
    const colorClasses = {
        blue: 'bg-blue-100 text-blue-600',
        green: 'bg-green-100 text-green-600',
        purple: 'bg-purple-100 text-purple-600',
        orange: 'bg-orange-100 text-orange-600'
    };

    return (
        <div className="bg-white rounded-xl p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
                    <Icon className="w-5 h-5" />
                </div>
                {trend && (
                    <div className={`flex items-center gap-1 text-xs font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}>
                        {trend === 'up' ? (
                            <ArrowUpRight className="w-3 h-3" />
                        ) : (
                            <ArrowDownRight className="w-3 h-3" />
                        )}
                    </div>
                )}
            </div>
            <p className="text-2xl font-bold text-gray-800">{value}</p>
            <p className="text-sm text-gray-500">{label}</p>
        </div>
    );
};

export default CourseAnalyticsDashboard;
